import { subCategoryFilter } from "app/filters/ngFilters";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { IndustryFilterKey } from "../configuration/constants";
import { FILTERS_OBJECT, INDUSTRY_FILTER_KEYS } from "../configuration/constants";
import type { CompetitiveFilterType } from "../types/common";

import useCompetitiveQueryParams from "./useCompetitiveQueryParams";

type HookReturnType = {
    onSelectCategory: (category: string | null) => void;
    onSelectIndustryTaxonomy: (nodeId: string | null) => void;
    onSearchTermFilterChange: (category: string) => void;
    /**
     * The legacy chip's label, derived from the id itself. Taxonomy ids are opaque and carry no label,
     * so that dropdown names its own selection from the tree it already renders.
     */
    selectedCategory: string | null;
    selectedCategoryId?: string;
    selectedIndustryTaxonomyId?: string;
    searchValue?: string;
};

const useCompetitiveFilter = (): HookReturnType => {
    const { availableFilters } = useCompetitiveQueryParams();

    const createFiltersForUrl = (filters: CompetitiveFilterType) =>
        Object.keys(filters)
            .map((filter) => {
                const { action, value } = filters[filter];
                return [filter, action, value].join(";");
            })
            .join("+");

    const selectIndustry = (key: IndustryFilterKey, value: string | null) => {
        const filters = { ...availableFilters };

        INDUSTRY_FILTER_KEYS.forEach((industryKey) => delete filters[industryKey]);

        if (value) {
            filters[key] = { ...FILTERS_OBJECT[key], value };
        }

        proNavigator.updateParams({
            similarsites_filters: createFiltersForUrl(filters),
        });
    };

    const onSearchTermFilterChange = (value: string) => {
        const filters = { ...availableFilters };

        if (value === "") {
            delete filters.domain;
        } else {
            filters.domain = {
                ...FILTERS_OBJECT.domain,
                value,
            };
        }
        proNavigator.updateParams({
            similarsites_filters: createFiltersForUrl(filters),
        });
    };

    const selectedCategoryValue = availableFilters.category?.value;

    return {
        onSelectCategory: (value) => selectIndustry("category", value),
        onSelectIndustryTaxonomy: (nodeId) => selectIndustry("industryTaxonomy", nodeId),
        onSearchTermFilterChange,
        selectedCategory: selectedCategoryValue ? decodeURIComponent(subCategoryFilter()(selectedCategoryValue)) : null,
        selectedCategoryId: selectedCategoryValue,
        selectedIndustryTaxonomyId: availableFilters.industryTaxonomy?.value,
        searchValue: availableFilters.domain?.value,
    };
};

export default useCompetitiveFilter;
